/**
 * feat-235 — the D2 violation banner.
 *
 * Every tenant is supposed to have exactly one `is_default = 1` track. When it
 * doesn't, the backend says so in `default_track_state`; before that field
 * existed, a broken tenant returned a bare empty list and rendered as "no tracks
 * authored yet" — the ordinary state of a brand-new tenant.
 *
 * WHY THIS IS THE DANGER FAMILY AND NOT THE `preview` SLATE the "in no track"
 * filter uses. Slate there is deliberate: a lesson in no track is a legitimate
 * state (a draft, a seasonal lesson pulled for the winter) and painting a
 * legitimate state as a warning teaches authors to "fix" drafts into tracks.
 * This is the opposite — a broken tenant, actionable only by engineering.
 * Reusing slate here would be a category error in the other direction.
 *
 * WHY THE COPY TELLS THE AUTHOR *NOT* TO CREATE A TRACK. On a `missing_no_tracks`
 * tenant the obvious repair is the one that destroys the evidence:
 * `create_learn_track` hard-codes `is_default = 0` (deliberately — the flag is
 * migration 114's to set), so a hand-made "Core" flips the tenant to
 * `missing_has_tracks`, the empty state stops firing, and `delete_learn_track`'s
 * 409 guard — which keys on `is_default` — never engages again. Every track on
 * that tenant, including the one the author believes is Core, becomes deletable.
 * The symptom disappears while the fault deepens, and the author cannot tell.
 * That is why the banner names the tenant id: it is the only thing engineering
 * can act on, and it is on screen so it can be copied.
 *
 * The `+ New Track` control is suppressed alongside this banner for
 * `missing_no_tracks` — see `TracksTab`. A banner alone would leave the
 * evidence-destroying action as the most prominent control on an empty screen.
 *
 * THE DEFAULT TRACK IS NOT A CATCH-ALL, and no copy here may imply it is. A
 * lesson removed from its last track becomes "in no Track"; it does not fall
 * back into the default.
 *
 * THE PROPERTY, stated so it survives without a search pattern to run:
 * `learn_track_lesson` has exactly ONE runtime write path — `replace_track_lessons`
 * (`admin_quiz_content.py:1668` DELETE, `:1672` INSERT), the explicit whole-list
 * PUT an author triggers. Migration 114 (`114_learn_track.sql:343`) writes once at
 * deploy time and the breakglass down-file drops the table; neither runs while
 * someone is authoring. To re-derive it, ENUMERATE every non-test mention of the
 * table and classify them — do not grep for a write verb. `INSERT INTO` alone
 * misses the migration's `INSERT IGNORE INTO` and this handler's own `DELETE`,
 * which is how the first version of this note undercounted the population while
 * happening to reach the right answer.
 *
 * WHY IT MATTERS HERE. An earlier draft of the `missing_has_tracks` sentence
 * called the default "the Track that holds Lessons filed nowhere else", which
 * contradicted the "In no Track" filter, its count line and its empty state — all
 * rendering one pane to the right of this banner. The author's available
 * conclusions were "the filter is broken" or "the default is absorbing my
 * drafts", the second being the exact anxiety that filter's design prevents. The
 * alert's credibility is what would have sold it: a false clause is noise in a
 * banner nobody trusts and damage in one that is shouting about a real fault.
 */

import React from 'react';
import type { DefaultTrackState } from '../../../../services/quizContentAdminService';
import { QUIZ_TERMS } from '../../quizTerminology';

/** The one state where creating a track quietly makes the fault harder to find. */
export function suppressesTrackCreation(state: DefaultTrackState | undefined): boolean {
  return state === 'missing_no_tracks';
}

export const DefaultTrackAlert: React.FC<{
  state: DefaultTrackState;
  tenantId: string;
  trackCount: number;
}> = ({ state, tenantId, trackCount }) => {
  if (state === 'ok') return null;

  let headline: React.ReactNode;
  let detail: React.ReactNode;
  // DERIVED IN THE SAME BRANCH AS THE HEADLINE, deliberately. This used to be a
  // separate two-way branch over the same variable, which is a shape that reads
  // as safe and isn't: two independent decisions over one input drift the moment
  // the input grows a value only one of them knows about. It did — an
  // unrecognised state took the `multiple_defaults` headline and the "none marked
  // default" note, contradicting itself inside one banner. Pairing them here
  // makes that unrepresentable rather than merely fixed.
  let countNote: string | null = null;
  const tracksWord = trackCount === 1 ? QUIZ_TERMS.track.singular : QUIZ_TERMS.track.plural;

  if (state === 'missing_no_tracks') {
    headline = (
      <>
        This tenant is missing its default {QUIZ_TERMS.track.singular}, and has no{' '}
        {QUIZ_TERMS.track.plural} at all.
      </>
    );
    detail = (
      <>
        Every tenant is set up with one, so something went wrong before you got here — which is
        also why there's nothing to author on this screen yet.{' '}
        <b>Making a {QUIZ_TERMS.track.singular} won't fix it.</b> One you create is never the
        default, so the rule that stops the default being deleted would stay switched off, and
        this screen would look repaired while it isn't.
      </>
    );
  } else if (state === 'missing_has_tracks') {
    headline = (
      <>
        This tenant has {QUIZ_TERMS.track.plural}, but none of them is the default one.
      </>
    );
    detail = (
      <>
        The default is the one {QUIZ_TERMS.track.singular} every tenant starts with, and it's
        meant to be undeletable — right now nothing here is. Carry on authoring if you need to;
        just don't count on anything being protected.
      </>
    );
    if (trackCount > 0) countNote = `${trackCount} ${tracksWord}, none marked default`;
  } else if (state === 'multiple_defaults') {
    headline = (
      <>
        This tenant has more than one default {QUIZ_TERMS.track.singular}.
      </>
    );
    detail = (
      <>
        The database is supposed to make that impossible, so something larger is off. Authoring
        still works, but which {QUIZ_TERMS.track.singular} counts as the default is now
        unpredictable.
      </>
    );
    if (trackCount > 0) countNote = `${trackCount} ${tracksWord}, more than one marked default`;
  } else {
    // A state this build does not know. Live the moment slice 6 adds `granted`
    // — the program doc has it planned — and the previous catch-all `else`
    // asserted "more than one default" about it, confidently and wrongly.
    //
    // Naming the raw value is the whole content: it is what engineering needs
    // and it is the one thing that is certainly true. NO `countNote` here —
    // every phrasing of it makes a claim about the default that only the four
    // known states support. And no advice about whether authoring is safe,
    // because this build cannot know: silence is the honest answer, where the
    // old branch's confident sentence was not.
    const raw = String(state);
    headline = (
      <>
        This tenant's {QUIZ_TERMS.track.singular} setup is in a state this screen doesn't
        recognise.
      </>
    );
    detail = (
      <>
        The server reported <code className="bg-white/70 rounded px-1 py-0.5 font-mono">{raw}</code>,
        which is newer than this screen. Nothing here can tell you whether it's safe to keep
        authoring.
      </>
    );
  }

  return (
    <div
      role="alert"
      data-testid="default-track-alert"
      data-state={state}
      className="mx-6 mt-4 shrink-0 rounded-xl border border-danger-line/40 bg-danger-surface text-danger-text px-3.5 py-2.5 text-[12.5px]"
    >
      <div className="flex items-start gap-2.5">
        <span aria-hidden className="text-base leading-none mt-0.5">
          ⚠️
        </span>
        <div className="min-w-0">
          <p className="font-bold">{headline}</p>
          <p className="mt-1 opacity-95">{detail}</p>
          {/* The action goes LAST and alone, because it is the only thing on
              this banner the author can act on. The id is `select-all` so one
              click grabs it whole. */}
          <p className="mt-1.5">
            Send engineering this tenant id:{' '}
            <code className="bg-white/70 rounded px-1 py-0.5 font-mono select-all">{tenantId}</code>
            {countNote && <span className="opacity-80"> ({countNote})</span>}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DefaultTrackAlert;
